/**
 * CD image source: .cue + .bin, a lone .bin, or a plain .iso.
 *
 * Produces the same `{path, size, open()}` descriptors as the folder and zip
 * sources, so everything downstream — quarantine, validation, batching, the
 * vault — is shared. The CD-specific parts are only three:
 *
 *  1. **Geometry.** A raw MODE1/2352 rip stores 2352 bytes per sector and we
 *     want 2048 of them. See `cue.ts`.
 *  2. **Case.** ISO 9660 level 1 names are upper-case by specification, so a
 *     disc yields `MASTER.DAT` and `DATA/SOUND/MUSIC/01HUB.ACM`. The engine is
 *     configured with `master_dat=master.dat` and `music_path1=data/sound/music/`
 *     and MEMFS is case-sensitive, so every path is lower-cased on the way in.
 *     This is not cosmetic: skip it and the game boots to a black screen.
 *  3. **Completeness.** A disc is not an install. We report what the disc
 *     actually carries rather than assuming the CD layout matches a hard-drive
 *     one — see `assessDisc()`.
 */

import { firstDataTrack, parseCue, CueParseError, CANDIDATE_GEOMETRIES, type SectorGeometry } from './cue';
import { extentBlob, geometryFits, readVolume, type IsoEntry, type IsoTrack, type IsoVolume } from './iso';
import type { SourceFile } from './index';

export class CdImportError extends Error {
  /** Set when a cue names a .bin the user did not hand us, so the UI can ask for it. */
  readonly missingFile?: string;
  constructor(message: string, missingFile?: string) {
    super(message);
    this.missingFile = missingFile;
  }
}

const IMAGE_EXT = /\.(bin|iso|img|mdf)$/i;
const CUE_EXT = /\.cue$/i;

export function isCueFile(f: File): boolean {
  return CUE_EXT.test(f.name);
}
export function isImageFile(f: File): boolean {
  return IMAGE_EXT.test(f.name);
}
/** True when a drop or file-pick looks like a CD image rather than a zip/folder. */
export function looksLikeCd(files: File[]): boolean {
  return files.some((f) => isCueFile(f) || isImageFile(f));
}

/** Matches a cue's FILE entry against what the user gave us. */
function resolveImage(name: string, images: File[]): File | undefined {
  // Cues written on Windows can carry a full path or backslashes; only the
  // basename is meaningful once the file is in the browser.
  const base = name.replace(/\\/g, '/').split('/').pop()!.toLowerCase();
  return (
    images.find((f) => f.name.toLowerCase() === base) ??
    // A .bin renamed on the way out of an archive still matches by stem.
    images.find((f) => f.name.toLowerCase().replace(IMAGE_EXT, '') === base.replace(IMAGE_EXT, ''))
  );
}

export interface DiscTrackChoice {
  track: IsoTrack;
  /** Human-readable account of how we chose the geometry, for the import log. */
  notes: string[];
}

/**
 * Works out where the filesystem is and how the sectors are laid out.
 *
 * The declared mode is a hint, not gospel: cues that say MODE1/2352 over a
 * cooked 2048 image (and the reverse) are common enough that we always probe
 * for `CD001` and prefer what the bytes say.
 */
export async function openDisc(files: File[], cueText?: string): Promise<DiscTrackChoice> {
  const notes: string[] = [];
  const cue = files.find(isCueFile);
  const images = files.filter(isImageFile);

  let image: File | undefined;
  // Kept as a sector index, not a byte offset: the byte offset depends on the
  // sector size, and the sector size is exactly what we are still deciding. A
  // cue that declares the wrong mode would otherwise place the track at the
  // wrong byte and the probe would find nothing at all.
  let startSector = 0;
  let declared: SectorGeometry | null = null;

  if (cue) {
    const sheet = (() => {
      try {
        return parseCue(cueText ?? '');
      } catch (e) {
        throw new CdImportError(e instanceof CueParseError ? e.message : String(e));
      }
    })();
    const audio = sheet.tracks.filter((t) => t.geometry === null);
    const track = firstDataTrack(sheet);
    if (!track) {
      throw new CdImportError(
        `${cue.name} describes ${sheet.tracks.length} track(s), all of them audio. There is no data track to read a game off.`
      );
    }
    if (audio.length) notes.push(`Skipped ${audio.length} audio track(s) — only the data track holds files.`);
    if (sheet.files.length > 1) notes.push(`Cue references ${sheet.files.length} files; the data track is in "${track.fileName}".`);

    image = resolveImage(track.fileName, images);
    if (!image) {
      throw new CdImportError(
        `${cue.name} points at "${track.fileName}", which wasn't included. Drop the .cue and the .bin together, or pick the .bin now.`,
        track.fileName
      );
    }
    startSector = track.startSector;
    declared = track.geometry;
    if (startSector > 0) notes.push(`Data track starts ${startSector} sectors into the file.`);
    if (startSector * track.geometry!.rawSize >= image.size) {
      throw new CdImportError(
        `${cue.name} places the data track ${startSector} sectors in, but ${image.name} is only ${image.size} bytes. ` +
          'The .cue and .bin do not belong to each other.'
      );
    }
  } else {
    image = images[0];
    if (!image) throw new CdImportError('No .bin, .iso or .cue found in that selection.');
    notes.push(`No .cue supplied — detecting the sector layout of ${image.name} directly.`);
  }

  // Probe: declared geometry first (cheap confirmation), then the candidates.
  const order = declared ? [declared, ...CANDIDATE_GEOMETRIES.filter((g) => g !== declared)] : CANDIDATE_GEOMETRIES;
  for (const geometry of order) {
    const base = startSector * geometry.rawSize;
    if (base < image.size && (await geometryFits(image, base, geometry))) {
      if (declared && geometry !== declared && geometry.rawSize !== declared.rawSize) {
        notes.push(
          `The .cue declares ${declared.rawSize}-byte sectors but the image has ${geometry.rawSize}-byte ones. Trusting the image.`
        );
      }
      if (image.size % geometry.rawSize !== 0) {
        notes.push(`Image is not a whole number of ${geometry.rawSize}-byte sectors; the last partial sector is ignored.`);
      }
      return { track: { blob: image, base, geometry }, notes };
    }
  }

  throw new CdImportError(
    `${image.name} has no ISO 9660 filesystem at sector 16 in any layout we recognise (2048, 2352 or 2336 bytes per sector). ` +
      'If this is a compressed or protected image format (.mdf/.nrg/.ccd), convert it to .bin/.cue or .iso first.'
  );
}

// --- disc → SourceFile[] -----------------------------------------------------

/**
 * Game data we take off a disc. Everything else on it is installers and manuals.
 *
 * The root `.dat` files are an allowlist rather than `*.dat`, which the folder
 * importer can afford to be. A hard-drive install only contains what the
 * installer chose to put there; a *disc* also carries the installer's own
 * `SETUP.DAT`, which matches "a .dat at the root" perfectly and is 221KB of
 * InstallShield manifest. Vaulting it is not fatal — the engine ignores it — but
 * MEMFS is rebuilt from the vault on every boot, so anything let through here is
 * copied in forever.
 */
const DISC_ROOT_DATS = /^(master|critter|patch\d+|f2_res)\.dat$/;

function wantedFromDisc(path: string): boolean {
  const p = path.toLowerCase();
  if (!p.includes('/')) return DISC_ROOT_DATS.test(p);
  return p.startsWith('data/');
}

export interface DiscAssessment {
  label: string;
  /** Files the disc carries that we would import, lower-cased. */
  paths: string[];
  masterBytes: number;
  critterBytes: number;
  musicTracks: number;
  hasPatch: boolean;
  /** Blocking problems — the import cannot produce a playable game. */
  problems: string[];
  /** Things worth saying out loud that are not blocking. */
  notes: string[];
}

/**
 * Honest completeness report for a disc.
 *
 * A retail Fallout 2 CD is not laid out like a hard-drive install: the game data
 * is two .dat files at the root plus a loose `DATA\SOUND\MUSIC` tree, and the
 * rest of the disc is the installer, DirectX 5, the manual and an Interplay
 * demo. The install program copies the .dat files across and creates empty
 * scratch directories — it does not unpack anything, so there is no third
 * category of file that only a "full install" would produce. That is why a
 * disc import can be complete, and why saying so requires checking rather than
 * hoping.
 */
export function assessDisc(volume: IsoVolume, kept: IsoEntry[]): DiscAssessment {
  const byPath = new Map(kept.map((e) => [e.path.toLowerCase(), e]));
  const size = (p: string) => byPath.get(p)?.size ?? 0;
  const masterBytes = size('master.dat');
  const critterBytes = size('critter.dat');
  const musicTracks = kept.filter((e) => /^data\/sound\/music\/.+\.acm$/i.test(e.path)).length;

  const problems: string[] = [];
  const notes: string[] = [];
  if (!masterBytes) problems.push('master.dat is not on this disc.');
  if (!critterBytes) problems.push('critter.dat is not on this disc.');

  // The retail FO2 disc carries a 333MB master.dat and a 167MB critter.dat;
  // FO1's are ~300MB and ~77MB. A wildly smaller one is a demo or stripped rip.
  if (masterBytes && masterBytes < 30 * 1024 * 1024) {
    problems.push(
      `master.dat is only ${(masterBytes / 1048576).toFixed(0)}MB — this looks like a demo or a stripped rip.`
    );
  }
  if (critterBytes && critterBytes < 10 * 1024 * 1024) {
    problems.push(
      `critter.dat is only ${(critterBytes / 1048576).toFixed(0)}MB — character art will be missing.`
    );
  }
  if (!musicTracks) {
    notes.push('No music tracks found on the disc. The game runs silent but plays.');
  } else if (musicTracks < 15) {
    notes.push(`Only ${musicTracks} music tracks found; the retail disc carries 19.`);
  }

  return {
    label: volume.label || 'untitled disc',
    paths: kept.map((e) => e.path.toLowerCase()),
    masterBytes,
    critterBytes,
    musicTracks,
    hasPatch: byPath.has('patch000.dat'),
    problems,
    notes,
  };
}

export interface CdLoadResult {
  files: SourceFile[];
  notes: string[];
  assessment: DiscAssessment;
}

export async function filesFromCd(input: File[]): Promise<CdLoadResult> {
  const files = Array.from(input);
  const cue = files.find(isCueFile);
  const { track, notes } = await openDisc(files, cue ? await cue.text() : undefined);
  const volume = await readVolume(track);

  notes.unshift(
    `Disc "${volume.label || 'untitled'}" — ${volume.entries.filter((e) => !e.isDirectory).length} files, ` +
      `${track.geometry.rawSize}-byte sectors${volume.joliet ? ', Joliet names' : ', ISO 9660 names'}.`
  );
  if (!volume.joliet) {
    notes.push('No Joliet directory on this disc, so names come from the ISO 9660 tree and are lower-cased for the engine.');
  }

  const kept = volume.entries.filter((e) => !e.isDirectory && wantedFromDisc(e.path));
  const assessment = assessDisc(volume, kept);

  if (assessment.problems.length) {
    throw new CdImportError(
      `This disc does not contain a usable Fallout install: ${assessment.problems.join(' ')} ` +
        (assessment.label ? `(volume label: ${assessment.label})` : '')
    );
  }

  const sourceFiles: SourceFile[] = kept.map((entry) => ({
    path: entry.path.toLowerCase(),
    size: entry.size,
    open: () => extentBlob(track, entry),
  }));

  return { files: sourceFiles, notes: [...notes, ...assessment.notes], assessment };
}
